(() => {
  const pendingEvents = [];
  const contactContextKey = 'wps:fitment_contact_context';
  const contactPendingKey = 'wps:fitment_contact_pending';
  const contactFallbackSuccessKey = 'wps:fitment_contact_success_without_pending';
  const contactDraftKey = 'wps:fitment_contact_draft';
  let retryTimer;

  const flush = () => {
    const publisher = window.Shopify?.analytics?.publish;
    if (!publisher) return false;
    while (pendingEvents.length) {
      const event = pendingEvents.shift();
      publisher(event.name, event.payload);
    }
    return true;
  };

  const publish = (name, payload) => {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(name, payload);
      return;
    }
    pendingEvents.push({ name, payload });
    if (retryTimer) return;
    let attempts = 0;
    retryTimer = window.setInterval(() => {
      attempts += 1;
      if (flush() || attempts >= 40) {
        window.clearInterval(retryTimer);
        retryTimer = undefined;
      }
    }, 250);
  };

  const readSession = (key) => {
    try {
      const value = window.sessionStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  };

  const writeSession = (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  };

  const removeSession = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  };

  const createRequestId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const sourceType = () => {
    if (document.querySelector('[data-wps-product], [data-wps-product-reference]')) return 'product';
    if (document.querySelector('[data-wps-model-reference]')) return 'model';
    if (document.querySelector('[data-wps-parts-index]')) return 'parts_index';
    if (window.location.pathname.startsWith('/blogs/')) return 'article';
    if (window.location.pathname.startsWith('/collections/')) return 'collection';
    return 'other';
  };

  const allowedEntryModes = new Set(['equipment', 'problem', 'unknown']);
  const allowedFamilies = new Set(['softener', 'cartridge', 'housing', 'uv', 'ro', 'tank', 'dosing', 'plumbing', 'system']);
  const allowedLabelStatuses = new Set(['unknown', 'readable', 'unreadable', 'missing']);
  const allowedResultStates = new Set([
    'identification_needed',
    'ambiguous',
    'no_supported_match',
    'reference_only_or_unavailable',
    'documented_candidates',
  ]);
  const allowedSources = new Set(['finder', 'product', 'model', 'parts_index', 'article', 'collection', 'other', 'direct']);
  const intentFamilies = {
    identify: allowedFamilies,
    replacement: allowedFamilies,
    'unknown-task': allowedFamilies,
    'no-draw': new Set(['softener']),
    drain: new Set(['softener']),
    leak: new Set(['housing', 'plumbing']),
    slow: new Set(['ro']),
    alarm: new Set(['uv']),
  };
  const finderEventNames = {
    entry: 'wps:finder_entry',
    step: 'wps:finder_step',
    result: 'wps:finder_result',
    exit: 'wps:finder_exit',
    'wps:finder_entry': 'wps:finder_entry',
    'wps:finder_step': 'wps:finder_step',
    'wps:finder_result': 'wps:finder_result',
    'wps:finder_exit': 'wps:finder_exit',
    'wps:finder_guide_exit': 'wps:finder_guide_exit',
    'wps:finder_help_exit': 'wps:finder_help_exit',
    'wps:finder_model_exit': 'wps:finder_model_exit',
  };
  const familyLabels = {
    softener: 'Softener or control valve',
    cartridge: 'Filter cartridge',
    housing: 'Filter housing',
    uv: 'UV system',
    ro: 'RO membrane or system',
    tank: 'Tank or media',
    dosing: 'Dosing or instrumentation',
    plumbing: 'Plumbing or leak device',
    system: 'Treatment system',
  };
  const intentLabels = {
    identify: 'Identify my equipment',
    replacement: 'Choose a replacement part',
    'no-draw': 'Softener will not draw brine',
    drain: 'Softener keeps draining',
    leak: 'Filter housing leaks after a cartridge change',
    slow: 'RO system produces water slowly',
    alarm: 'UV system alarm or lamp warning',
    'unknown-task': 'My problem is not listed',
  };

  const sanitizeFinderContext = (value) => {
    if (!value || typeof value !== 'object') return {};
    const context = {};
    if (allowedEntryModes.has(value.entry_mode)) context.entry_mode = value.entry_mode;
    if (allowedFamilies.has(value.family_id)) context.family_id = value.family_id;
    if (allowedLabelStatuses.has(value.label_status)) context.label_status = value.label_status;
    if (allowedResultStates.has(value.result_state)) context.result_state = value.result_state;
    if (allowedSources.has(value.source_type)) context.source_type = value.source_type;
    if (
      context.family_id === 'softener'
      && typeof value.model_handle === 'string'
      && /^[a-z0-9-]+$/.test(value.model_handle)
    ) {
      context.model_handle = value.model_handle;
    }
    if (typeof value.intent_id === 'string' && context.family_id && intentFamilies[value.intent_id]?.has(context.family_id)) {
      context.intent_id = value.intent_id;
    }
    return context;
  };

  const hasFinderContext = (context) => ['entry_mode', 'family_id', 'intent_id', 'label_status', 'model_handle', 'result_state']
    .some((key) => Boolean(context[key]));

  const contextFromUrl = (url = window.location.href) => {
    const params = new URL(url, window.location.origin).searchParams;
    return sanitizeFinderContext({
      entry_mode: params.get('entry_mode'),
      family_id: params.get('family_id'),
      intent_id: params.get('intent_id'),
      label_status: params.get('label_status'),
      model_handle: params.get('model_handle'),
      result_state: params.get('result_state'),
    });
  };

  const mergeFinderContexts = (...contexts) => sanitizeFinderContext(
    contexts.reduce((merged, context) => ({ ...merged, ...sanitizeFinderContext(context) }), {}),
  );

  const finderContextValue = (context) => ['entry_mode', 'family_id', 'intent_id', 'label_status', 'model_handle', 'result_state']
    .filter((key) => context[key])
    .map((key) => `${key}=${context[key]}`)
    .join('; ');

  const finderContextSummary = (context) => [
    context.entry_mode,
    familyLabels[context.family_id],
    intentLabels[context.intent_id],
    context.label_status && `${context.label_status} label`,
    context.result_state && context.result_state.replaceAll('_', ' '),
  ].filter(Boolean).join(' · ');

  const selectedOptionId = (field, key) => field?.selectedOptions?.[0]?.dataset[key] || '';

  const initialize = () => {
    const product = document.querySelector('[data-wps-product], [data-wps-product-reference]');
    const model = document.querySelector('[data-wps-model-reference]');
    if (product?.matches('[data-wps-product-reference]')) {
      const publishProductReference = () => window.setTimeout(() => {
        publish('wps:catalog_reference_viewed', {
          sku: product.dataset.sku,
          vendor: product.dataset.vendor,
        });
      }, 1000);
      if (document.readyState === 'complete') publishProductReference();
      else window.addEventListener('load', publishProductReference, { once: true });
    }

    document.addEventListener('wps:finder_event', (event) => {
      const eventName = finderEventNames[event.detail?.name];
      if (!eventName) return;
      const context = sanitizeFinderContext(event.detail);
      publish(eventName, {
        entry_mode: context.entry_mode || 'unknown',
        family_id: context.family_id || '',
        intent_id: context.intent_id || '',
        label_status: context.label_status || 'unknown',
        result_state: context.result_state || '',
      });
    });

    const contactForm = document.getElementById('ContactForm');
    if (contactForm) {
      const contextLabel = contactForm.querySelector('[data-wps-contact-context]');
      const contextValue = contactForm.querySelector('[data-wps-contact-context-value]');
      const familyField = contactForm.querySelector('[data-wps-contact-family]');
      const modelField = contactForm.querySelector('[data-wps-contact-model]');
      const intentField = contactForm.querySelector('[data-wps-contact-intent]');
      const labelStatusField = contactForm.querySelector('[data-wps-contact-label-status]');
      const modelOptionForHandle = (handle) => [...contactForm.querySelectorAll('[data-wps-model-handle]')]
        .find((option) => option.dataset.wpsModelHandle === handle);
      const restoreDraft = () => {
        if (!document.querySelector('[data-wps-contact-error]')) return;
        const draft = readSession(contactDraftKey);
        if (!draft || typeof draft !== 'object') return;
        Object.entries(draft).forEach(([name, value]) => {
          const field = contactForm.elements.namedItem(name);
          const isUnknownSelect = field?.tagName === 'SELECT' && field.value === 'Unknown';
          if (
            field
            && typeof field.value === 'string'
            && (!field.value || isUnknownSelect)
            && typeof value === 'string'
          ) {
            field.value = value;
          }
        });
      };
      const saveDraft = () => {
        const draft = {};
        [...contactForm.elements].forEach((field) => {
          if (!field.name || field.name === 'contact[Finder context]' || field.type === 'submit') return;
          if (typeof field.value === 'string') draft[field.name] = field.value;
        });
        writeSession(contactDraftKey, draft);
      };
      restoreDraft();
      const contactHasError = Boolean(document.querySelector('[data-wps-contact-error]'));
      let context = mergeFinderContexts(readSession(contactContextKey), contactHasError ? {} : contextFromUrl());
      if (hasFinderContext(context) && !context.source_type) context.source_type = 'finder';

      if (context.model_handle && (context.family_id !== 'softener' || !modelOptionForHandle(context.model_handle))) delete context.model_handle;
      if (!selectedOptionId(familyField, 'wpsFamilyId') && context.family_id) {
        const option = [...familyField.options].find((item) => item.dataset.wpsFamilyId === context.family_id);
        if (option) familyField.value = option.value;
      }
      if (!modelField.value && context.model_handle) {
        const option = modelOptionForHandle(context.model_handle);
        if (option) modelField.value = option.value;
      }
      if (!selectedOptionId(intentField, 'wpsIntentId') && context.intent_id) {
        const option = [...intentField.options].find((item) => item.dataset.wpsIntentId === context.intent_id);
        if (option) intentField.value = option.value;
      }
      if (selectedOptionId(labelStatusField, 'wpsLabelStatus') === 'unknown' && context.label_status && context.label_status !== 'unknown') {
        const option = [...labelStatusField.options].find((item) => item.dataset.wpsLabelStatus === context.label_status);
        if (option) labelStatusField.value = option.value;
      }

      const syncContactContext = () => {
        const selectedFamily = selectedOptionId(familyField, 'wpsFamilyId');
        const selectedIntent = selectedOptionId(intentField, 'wpsIntentId');
        const selectedLabelStatus = selectedOptionId(labelStatusField, 'wpsLabelStatus');
        const matchingModel = [...contactForm.querySelectorAll('[data-wps-model-handle]')]
          .find((option) => option.value === modelField.value);
        const nextContext = { ...context };
        if (selectedFamily) nextContext.family_id = selectedFamily;
        else delete nextContext.family_id;
        if (selectedIntent) nextContext.intent_id = selectedIntent;
        else delete nextContext.intent_id;
        if (selectedLabelStatus && selectedLabelStatus !== 'unknown') nextContext.label_status = selectedLabelStatus;
        else delete nextContext.label_status;
        if (nextContext.family_id === 'softener' && matchingModel) nextContext.model_handle = matchingModel.dataset.wpsModelHandle;
        else delete nextContext.model_handle;
        context = sanitizeFinderContext(nextContext);
        if (hasFinderContext(context) && !context.source_type) context.source_type = 'finder';

        const summary = finderContextSummary(context);
        contextLabel.textContent = summary ? `${context.source_type === 'finder' ? 'Finder' : 'Identification'} context: ${summary}` : '';
        contextLabel.hidden = !summary;
        contextValue.value = finderContextValue(context);
      };

      syncContactContext();
      [familyField, modelField, intentField, labelStatusField].forEach((field) => {
        field.addEventListener('change', syncContactContext);
      });
      contactForm.addEventListener('submit', () => {
        syncContactContext();
        saveDraft();
        if (hasFinderContext(context)) writeSession(contactContextKey, context);
        writeSession(contactPendingKey, {
          request_id: createRequestId(),
          source_type: context.source_type || 'direct',
        });
        removeSession(contactFallbackSuccessKey);
      });
    }

    const successMarker = document.querySelector('[data-wps-contact-success]');
    if (successMarker) {
      const pending = readSession(contactPendingKey);
      const dedupeKey = pending?.request_id
        ? `wps:fitment_contact_success:${pending.request_id}`
        : contactFallbackSuccessKey;

      if (!readSession(dedupeKey)) {
        publish('wps:fitment_request_submitted', {
          source_type: pending?.source_type || 'direct',
        });
        writeSession(dedupeKey, { emitted: true });
      }
      writeSession(contactFallbackSuccessKey, { emitted: true });

      removeSession(contactPendingKey);
      removeSession(contactContextKey);
      removeSession(contactDraftKey);
    }

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link) return;
      if (link.matches('[data-wps-manual-link]')) {
        publish('wps:manual_opened', {
          sku: product?.dataset.sku || '',
          vendor: product?.dataset.vendor || model?.dataset.vendor || '',
          source_type: sourceType(),
        });
      } else if (link.href.includes('/pages/contact')) {
        const storedContext = sanitizeFinderContext(readSession(contactContextKey));
        const linkedContext = mergeFinderContexts(storedContext, contextFromUrl(link.href));
        const context = hasFinderContext(linkedContext)
          ? { ...linkedContext, source_type: linkedContext.source_type || 'finder' }
          : { source_type: sourceType() };
        writeSession(contactContextKey, context);
        publish('wps:fitment_contact_opened', {
          source_type: context.source_type,
          entry_mode: context.entry_mode || 'unknown',
          family_id: context.family_id || '',
          intent_id: context.intent_id || '',
          result_state: context.result_state || '',
        });
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();
